import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Grid, TextField, Button, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { SUPER_ADMIN } from "common/constant";
import {
  getOptionLabel,
  getOptionValue,
  otherUtilityBillingOptions,
  waterDeveloperTypeEnum,
} from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyGetOtherBillingReportsByUtilityId } from "apis/OtherBillingReport/otherBillingReport";
import { useLazyDownloadExcelQuery } from "slices/api";

const OtherUtilityBillingCalculation = ({ t }) => {
  const { utilityRoles } = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    getOtherBillingReports,
    {
      data: { data: billingReportsData } = {},
      isLoading: isBillingReportLoading,
      isFetching: isBillingReportFetching,
    },
  ] = useLazyGetOtherBillingReportsByUtilityId();

  const sm = useMediaQuery("(max-width: 599px)");

  const [billCycleId, setBillCycleId] = useState("");
  const [waterDeveloperType, setWaterDeveloperType] = useState(
    waterDeveloperTypeEnum[0]
  );
  const [{ utilityId, subscriptionType }, setUtility] = useState({
    utilityId: "",
    subscriptionType: "",
  });
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();
  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType: currentSubscriptionType }) =>
                currentSubscriptionType === subscriptionType
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const otherUtilityFilteredOptions = useMemo(() => {
    const utilityRoleIds = utilityRoles.map(({ utilityId }) => utilityId);
    return roles.includes(SUPER_ADMIN)
      ? otherUtilityBillingOptions
      : otherUtilityBillingOptions.filter(({ value }) =>
          utilityRoleIds.includes(value)
        );
  }, [otherUtilityBillingOptions, utilityRoles]);

  const getOtherUtilityReportsData = async (query) => {
    try {
      await getOtherBillingReports(query);
    } catch (error) {}
  };

  const downloadBtnHandler = async () => {
    try {
      if (utilityId) {
        await downloadExcel({
          endpoint: "/report/other-utility-billing-report-Excel",
          sheetName: `${getOptionLabel(
            "otherUtilityApplicationOptions",
            utilityId
          )} ${isWaterUtility ? waterDeveloperType.label : ""} Billing ${
            updatedBillingCycleOptions.find(
              ({ value }) => value === billCycleId
            ).label
          }`,
          query: {
            utility: utilityId,
            billingCycleId: billCycleId,
            ...(isWaterUtility && {
              waterDeveloperType: waterDeveloperType.value,
            }),
          },
        });
      }
    } catch (error) {}
  };

  const { isDcsUtility, isWaterUtility, isSewageUtility, isSolidWasteUtility } =
    useMemo(
      () => ({
        isDcsUtility:
          utilityId === getOptionValue("otherUtilityApplicationOptions", "DCS"),
        isWaterUtility:
          utilityId ===
          getOptionValue("otherUtilityApplicationOptions", "Water"),
        isSewageUtility:
          utilityId ===
          getOptionValue("otherUtilityApplicationOptions", "Sewage"),
        isSolidWasteUtility:
          utilityId ===
          getOptionValue("otherUtilityApplicationOptions", "Solid Waste"),
      }),
      [utilityId]
    );

  const columns = useMemo(
    () => [
      {
        title: "Sales Order Number",
        field: "salesOrderNumber",
      },
      {
        title: "Pricing Current Date",
        renderCell: ({ dcsPricingDate, billDate }) =>
          moment(isDcsUtility ? dcsPricingDate : billDate).isValid()
            ? convertUTCDateToLocalDate(
                isDcsUtility ? dcsPricingDate : billDate
              ).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Material",
        field: "material",
      },
      ...(isDcsUtility || isWaterUtility
        ? [
            {
              title: "Current Reading",
              field: "currentReading",
            },
            {
              title: `To be billed Qty ${isWaterUtility ? "in KL" : ""}`,
              field: "toBeBilledQty",
            },
            {
              title: `Customer Material Number (${
                isDcsUtility ? "Meter Number" : "Consumer Number"
              })`,
              field: isDcsUtility ? "meterNumber" : "consumerNumber",
            },
            { title: "Storage Location", field: "storageLocation" },
          ]
        : []),
      ...(isDcsUtility
        ? [
            {
              title: "Actual Demand",
              field: "actualMaxDemand",
            },
            {
              title: "Meter Charges",
              field: "meterCharges",
            },
            { title: "Demand Charges", field: "demandCharges" },
          ]
        : []),
      ...(isSewageUtility
        ? [
            {
              title: "Water Bill Date",
              renderCell: ({ waterBillDate }) =>
                moment(waterBillDate).isValid()
                  ? convertUTCDateToLocalDate(waterBillDate).format(
                      "DD-MM-YYYY"
                    )
                  : "N/A",
            },
          ]
        : []),
      ...(isSolidWasteUtility
        ? [{ title: "Serial Number", field: "serialNumber" }]
        : []),
      { title: "Plant", field: "plant" },
      {
        title: isDcsUtility ? "Billing Date" : "Bill Date",
        renderCell: ({ billDate }) =>
          moment(billDate).isValid()
            ? convertUTCDateToLocalDate(billDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: isDcsUtility ? "Billing Month" : "Billing Period",
        renderCell: ({ billingMonthOrPeriod }) =>
          moment(billingMonthOrPeriod).isValid()
            ? convertUTCDateToLocalDate(billingMonthOrPeriod).format(
                "DD-MM-YYYY"
              )
            : "N/A",
      },
      ...(isWaterUtility || isSewageUtility || isSolidWasteUtility
        ? [{ title: "Name", field: "consumerName" }]
        : []),
      { title: "Invoice Number", field: "invoiceNumber" },
      {
        title: "Invoice Date",
        renderCell: ({ invoiceDate }) =>
          moment(invoiceDate).isValid()
            ? convertUTCDateToLocalDate(invoiceDate).format("DD-MM-YYYY")
            : "N/A",
      },
    ],
    [isDcsUtility, isWaterUtility, isSewageUtility, isSolidWasteUtility]
  );

  const handleClear = () => {
    const billCycleId =
      updatedBillingCycleOptions.find(
        ({ status }) => status !== getOptionValue("billingStatus", "Closed")
      )?.value ??
      updatedBillingCycleOptions.at(-1)?.value ??
      "";
    const utilityId = otherUtilityFilteredOptions[0].value;
    const subscriptionType = otherUtilityFilteredOptions[0].subscriptionType;
    setUtility({
      utilityId: utilityId,
      subscriptionType: subscriptionType,
    });
    setBillCycleId(billCycleId);
    getOtherUtilityReportsData({
      billingCycleId: billCycleId,
      utility: utilityId,
      ...(isWaterUtility && {
        waterDeveloperType: waterDeveloperType.value,
      }),
    });
    handleResetPage();
  };

  useEffect(() => {
    if (otherUtilityFilteredOptions.length) {
      setUtility({
        utilityId: otherUtilityFilteredOptions[0].value,
        subscriptionType: otherUtilityFilteredOptions[0].subscriptionType,
      });
    }
  }, [otherUtilityFilteredOptions]);

  useEffect(() => {
    if (updatedBillingCycleOptions.length && subscriptionType) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      getOtherUtilityReportsData({
        billingCycleId,
        utility: utilityId,
        ...(isWaterUtility && {
          waterDeveloperType: waterDeveloperType.value,
        }),
      });
    }
  }, [updatedBillingCycleOptions]);

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isBillingReportFetching ||
          isBillingReportLoading ||
          isExcelFileDownloading
        }
      />
      <BreadCrumb title={t("Billing Report")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getOtherUtilityReportsData({
            billingCycleId: billCycleId,
            utility: utilityId,
            ...(isWaterUtility && {
              waterDeveloperType: waterDeveloperType.value,
            }),
          });
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1 mt-2">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Utility")}
              onChange={(_, { value, subscriptionType }) => {
                setUtility({
                  utilityId: value,
                  subscriptionType: subscriptionType,
                });
              }}
              options={otherUtilityFilteredOptions}
              value={
                otherUtilityFilteredOptions.find(
                  ({ value }) => value === utilityId
                ) ?? ""
              }
            />
          </Grid>

          {isWaterUtility && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Water Developer Type")}
                options={waterDeveloperTypeEnum}
                value={waterDeveloperType}
                onChange={(_, value) => setWaterDeveloperType(value)}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => setBillCycleId(value)}
              value={
                updatedBillingCycleOptions.find(
                  (status) => status.value === billCycleId
                ) ?? ""
              }
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
              options={updatedBillingCycleOptions}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              className=" me-2"
              type="submit"
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              onClick={handleClear}
              className=" me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadBtnHandler}
              disabled={!billCycleId}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={billingReportsData || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

OtherUtilityBillingCalculation.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OtherUtilityBillingCalculation);
